import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '~/lib/supabase';
import type { Database } from '~/types/database';

type Profile = Database['public']['Tables']['profiles']['Row'];

/*
 * DEMO BUILD. Nothing below was rewritten — it runs unchanged against the fake
 * client in src/lib/supabase.ts, which always reports one signed-in session
 * (the owner's admin account, see src/demo/client/auth.ts). Consequences:
 *   - `loading` settles on the first tick; the router never sees a null session.
 *   - the `profiles` lookup below hits the snapshot and finds the real row.
 *   - `signIn` still POSTs /api/login; the demo API answers 404, so the
 *     existing fail-open path runs and the fake `signInWithPassword` succeeds.
 *   - `signOut` resolves but the session is never cleared — deliberately, so a
 *     visitor can't strand themselves on a login screen no password can pass.
 *   - "View as" keeps working: it is purely client-side and is a useful way to
 *     show the developer how each role sees the app.
 */

/**
 * The legacy "Lead Sources" Marketing grant is a single checkbox that expands
 * to these 10 data-generated sub-tabs (ACCESS-CONTROL-PORT.md §2/§3). A user
 * granted "Lead Sources" can see all of them.
 */
export const MARKETING_LEAD_SOURCE_TABS = [
  'Past Customer', 'Plumber Referrals', 'Other Referrals',
  'Marketing - SEO', 'Marketing - GLS', 'Marketing - PPC',
  'Marketing - eLocal', 'Marketing - 33 Mile', 'Marketing - Service Direct', 'Marketing - Inquirly',
] as const;

export type AuthState = {
  session: Session | null;
  user: User | null;
  /** Effective profile — the previewed user when "View as" is active, else the real one. */
  profile: Profile | null;
  /** The signed-in admin's own profile (never the preview). */
  realProfile: Profile | null;
  /** Non-null while previewing another user's access ("View as"). */
  previewProfile: Profile | null;
  /** Admin-only: render the app as this user would see it (UI/permissions only — data is still yours). */
  setPreviewProfile: (p: Profile | null) => void;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  hasRole: (roleKey: string) => boolean;
  hasTabAccess: (dashboard: string, tab: string) => boolean;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [previewProfile, setPreviewProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    setPreviewProfile(null); // never carry a "View as" across an account change
    supabase
      .from('profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setProfile(data ?? null);
      });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // Effective profile drives all UI gating; previewing only works for real admins.
  const realIsAdmin = Array.isArray(profile?.staff_role_keys)
    && (profile?.staff_role_keys as string[] | null ?? []).includes('admin');
  const effectiveProfile = (realIsAdmin && previewProfile) ? previewProfile : profile;

  const value = useMemo<AuthState>(() => ({
    session,
    user: session?.user ?? null,
    profile: effectiveProfile,
    realProfile: profile,
    previewProfile: realIsAdmin ? previewProfile : null,
    setPreviewProfile,
    loading,
    signIn: async (email, password) => {
      // Go through the lockout-enforcing endpoint first. It records failures
      // server-side (10 tries → 15-min cooldown) and returns a session on
      // success. Fail OPEN: any transport/availability problem falls back to a
      // normal client sign-in so a broken endpoint can never lock everyone out.
      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        if (res.status === 200) {
          const { access_token, refresh_token } = await res.json();
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          return { error: error?.message ?? null };
        }
        if (res.status === 401 || res.status === 429) {
          const body = await res.json().catch(() => ({}));
          return { error: body.error ?? 'Sign-in failed.' };
        }
        // 400/404/500/etc → fall through to the fail-open path below.
      } catch {
        // network error → fail open
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    },
    signOut: async () => { setPreviewProfile(null); await supabase.auth.signOut(); },
    hasRole: (roleKey) => Array.isArray(effectiveProfile?.staff_role_keys)
      && (effectiveProfile?.staff_role_keys as string[] | null ?? []).includes(roleKey),
    hasTabAccess: (dashboard, tab) => {
      const allowed = (effectiveProfile?.allowed_tabs as Record<string, string[]> | null) ?? {};
      let tabs = allowed[dashboard];
      if (!Array.isArray(tabs)) return false;
      if (dashboard === 'marketing' && tabs.includes('Lead Sources')) {
        tabs = [...tabs, ...MARKETING_LEAD_SOURCE_TABS];
      }
      return tabs.includes(tab);
    },
  }), [session, profile, previewProfile, effectiveProfile, realIsAdmin, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
